import { Platform } from 'react-native';

export const Colors = {
  primary: '#FF6347', // Tomato
  secondary: '#FFD700', // Gold
  background: '#F5F5F5', // Light Gray
  text: '#333333',
  lightText: '#666666',
  textSecondary: '#78716c', // stone-500 (shared with Magazine/Paprika)
  white: '#FFFFFF',
  black: '#000000',
  success: '#3CB371', // MediumSeaGreen
  warning: '#FFD700', // Gold
  danger: '#DC143C', // Crimson
  border: '#d6d3d1', // stone-300 (shared with Magazine/Paprika)
  accent: '#C17F59', // Terracotta (shared with Paprika)
};

// Magazine-style color palette (from Figma design)
export const Magazine = {
  // Backgrounds
  background: '#fdfbf7', // Warm cream
  cardBg: '#ffffff', // White cards
  headerBg: '#ffffff', // White header

  // Text colors (stone palette)
  textPrimary: '#1c1917', // stone-900
  textSecondary: '#78716c', // stone-500
  textMuted: '#a8a29e', // stone-400
  textLight: '#d6d3d1', // stone-300

  // Borders
  border: '#d6d3d1', // stone-300
  borderLight: '#e7e5e4', // stone-200
  borderDark: '#292524', // stone-800

  // Day numbers
  dayNumber: '#e7e5e4', // stone-200

  // UI elements
  buttonDark: '#1c1917', // stone-900 for dark buttons
  buttonLight: '#f1f5f9', // slate-100 for light buttons

  // Shadows (as rgba strings for cross-platform)
  shadowColor: 'rgba(0, 0, 0, 0.1)',
};

export const Typography = {
  fontFamily: 'System',
  fontSize: {
    small: 12,
    medium: 16,
    large: 20,
    xLarge: 24,
  },
  fontWeight: {
    light: '300',
    regular: '400',
    bold: '700',
  },
};

export const Spacing = {
  tiny: 4,
  small: 8,
  medium: 16,
  large: 24,
  xLarge: 32,
};

export const BorderRadius = {
  small: 4,
  medium: 8,
  large: 12,
  xLarge: 24,
  round: 999,
};

// Magazine-specific typography (from Figma design)
export const MagazineTypography = {
  // Masthead
  mastheadSize: 51,
  mastheadWeight: '700' as const,

  // Subtitles
  subtitleSize: 12,

  // Day headers
  dayNameSize: 25.5,
  dayNameWeight: '700' as const,
  dayNumberSize: 40.8,
  dayNumberWeight: '400' as const,

  // Cards
  recipeTitleSize: 17,
  mealTypeSize: 8.5,
  cookTimeSize: 12,

  // Header
  headerTitleSize: 20.4,
  headerTitleWeight: '700' as const,
  headerSubtitleSize: 12,

  // Navigation
  navButtonSize: 10.2,
  navButtonWeight: '700' as const,
  weekRangeSize: 15.3,
  weekRangeWeight: '700' as const,
};

// Magazine-specific spacing (from Figma design)
export const MagazineSpacing = {
  contentPadding: 32,
  cardPadding: 12,
  sectionGap: 32,
  headerHeight: 101,
  mastheadHeight: 114,
};

// Magazine card shadows
export const MagazineShadows = {
  card: Platform.select({
    ios: {
      shadowColor: '#52443c',
      shadowOffset: { width: 0, height: 8 },
      shadowOpacity: 0.08,
      shadowRadius: 10,
    },
    android: {
      elevation: 8,
    },
    default: {
      boxShadow: '0 8px 10px rgba(82,68,60,0.08)',
    },
  }) as any,
  header: Platform.select({
    ios: {
      shadowColor: '#52443c',
      shadowOffset: { width: 0, height: 1 },
      shadowOpacity: 0.08,
      shadowRadius: 2,
    },
    android: {
      elevation: 2,
    },
    default: {
      boxShadow: '0 1px 2px rgba(82,68,60,0.08)',
    },
  }) as any,
};

// Recipe Browser typography (Mealime-style)
export const RecipeBrowserTypography = {
  // Header
  headerTitle: 28,
  headerTitleWeight: '700' as const,

  // Cards
  cardTitle: 15,
  cardTitleWeight: '500' as const,
  cookTime: 12,
  cookTimeWeight: '400' as const,

  // Filters
  filterChip: 14,
  filterChipWeight: '500' as const,

  // Search
  searchInput: 16,
  ingredientChip: 14,
};

// Paprika Design System - Digital Curator editorial aesthetic
export const Paprika = {
  // Surface hierarchy (paper stacking metaphor)
  background: '#fbf9f6',        // Warm cream - main canvas
  surface: '#fbf9f6',           // Base surface
  surfaceLight: '#f5f3f0',      // Subtle section backgrounds (alias for surfaceLow)
  surfaceLow: '#f5f3f0',        // Subtle section backgrounds
  surfaceLowest: '#ffffff',     // Cards that "float" - highest priority paper
  surfaceHighest: '#e4e2df',    // Inset elements, search bars
  surfaceMuted: '#f5f3f0',      // Highlights, chat bubbles (alias for surfaceLow)

  // Text (warm-tinted hierarchy)
  textPrimary: '#1b1c1a',       // Near black - headings, primary text
  onSurface: '#1b1c1a',         // Near black (Material alias)
  textSecondary: '#52443c',     // Warm brown - subtitles, meta info
  onSurfaceVariant: '#52443c',  // Warm brown (Material alias)
  textMuted: '#85746b',         // Outline tone - placeholders, hints

  // Primary (darker terracotta)
  accent: '#864e2c',            // Primary accent - active tabs, buttons
  primary: '#864e2c',           // Primary (Material alias)
  primaryContainer: '#a36642',  // Gradient endpoint for CTAs
  onPrimary: '#ffffff',         // Text on primary
  accentLight: '#ffdbc9',       // Light peach - accent backgrounds, badges
  accentOrange: '#E8734A',      // Orange - Pro badge, progress bars

  // Status
  destructive: '#ba1a1a',       // Warm-tuned red - remove, errors
  error: '#ba1a1a',             // Error (Material alias)
  success: '#78A55A',           // Warm sage green - success states
  successSoft: '#eaf1e1',       // Soft sage surface - in-stock marks, success tints

  // Household approval. Derived rather than reused: success (#78A55A) on
  // approvalYesTint measures ~2.3:1, under the 3:1 required for a non-text
  // icon (WCAG 1.4.11). approvalIdea* dresses proposed meals (F2).
  approvalYes: '#567a3c',
  approvalYesTint: '#eef5e6',
  approvalNo: '#9e4030',
  approvalNoTint: '#fbe9e6',
  // = PaprikaFreshness.soon.text. The handoff proposed #8a6a12, a
  // near-duplicate of a gold the ramp already carries — close enough to look
  // like a mistake, far enough to be one.
  approvalIdea: '#8a6a1c',
  approvalIdeaTint: '#fdf6e0',

  // Borders (ghost border philosophy)
  border: '#d7c2b8',            // Outline variant - card borders, dividers
  outlineVariant: '#d7c2b8',    // Ghost borders at 20% opacity
  outline: '#85746b',           // Stronger outlines
  borderLight: '#f0f0f0',       // Subtle separations

  // Tags & secondary surfaces
  secondaryContainer: '#e2dfde', // Tag backgrounds
  onSecondaryContainer: '#636262', // Tag text

  // Card backgrounds
  cardBg: '#ffffff',            // White cards (floating paper)

  // Pantry food tile background (neutral)
  foodTile: '#f4efe8',

  // AI / purple accent
  aiTint: '#f6f1fd',
  aiSoft: '#efe8fb',
  ai: '#6d44b8',
  aiDots: '#e0d3ef',

  // Warm surface sinks
  bgSunk: '#f3ece2',
  bgWash: '#f7f1e8',            // Warm inset wash - step rail, warm side panels
  brandSoft: '#fbe4d4',
  brandTint: '#fdf0e6',
  brandDeep: '#6f3f22',         // Deep terracotta - timer digits, emphasis on tint
  hairline: '#f3ebe1',          // Warm hairline - the only permitted 1px separator

  // Toast
  toastBg: '#1f1b17',           // Dark toast background
  toastAction: '#f0b28a',       // Toast action text (undo)
  toastText: '#ffffff',         // Toast message text

  // Glass / Translucent
  glassWhite: 'rgba(255,255,255,0.72)',  // Translucent white for floated chips

  // Recipe scoring colors
  scoreMid: '#c98a2f',               // Mid-range score color (60-79%) for recipe matching
};

// Paprika Gradients - terracotta and warm tones
export const PaprikaGradients = {
  cookCta: ['#864e2c', '#a36642'],  // Warm terracotta gradient for cook action buttons
};

// Entry-type tints (design handoff "entrées non-recette")
export const PaprikaEntry = {
  dining:   { tint: '#f6e6db', color: '#9a5730' },   // terracotta variant on primary-fixed-like tint
  leftover: { tint: '#e9f0dd', color: '#5d7c43' },   // sage on light green container
  note:     { tint: '#f3ece2', color: '#52443c' },   // on-surface-variant on bgSunk
  skip:     { tint: 'transparent', color: '#85746b' }, // outline, transparent + dashed border
} as const;

// Paprika Typography - Newsreader (serif) for editorial headlines, Inter (sans) for body
export const PaprikaTypography = {
  // Display (Serif) - Screen titles, hero recipe names
  displayLarge: {
    fontFamily: 'Newsreader-Italic',
    fontSize: 40,
    lineHeight: 48,
    fontWeight: '400' as const,
  },
  displayMedium: {
    fontFamily: 'Newsreader-Italic',
    fontSize: 32,
    lineHeight: 40,
    fontWeight: '400' as const,
  },

  // Heading (Serif) - Section headers like "Ingredients", "Preparation"
  headingLarge: {
    fontFamily: 'Newsreader-Regular',
    fontSize: 28,
    lineHeight: 36,
    fontWeight: '400' as const,
  },
  headingMedium: {
    fontFamily: 'Newsreader-Regular',
    fontSize: 24,
    lineHeight: 32,
    fontWeight: '400' as const,
  },

  // Title (Sans-serif) - Card titles, list items
  titleLarge: {
    fontFamily: 'Inter-Medium',
    fontSize: 17,
    lineHeight: 24,
    fontWeight: '500' as const,
  },
  titleMedium: {
    fontFamily: 'Inter-Medium',
    fontSize: 15,
    lineHeight: 22,
    fontWeight: '500' as const,
  },

  // Body (Sans-serif) - Regular content
  bodyLarge: {
    fontFamily: 'Inter-Regular',
    fontSize: 16,
    lineHeight: 24,
    fontWeight: '400' as const,
  },
  bodyMedium: {
    fontFamily: 'Inter-Regular',
    fontSize: 14,
    lineHeight: 20,
    fontWeight: '400' as const,
  },
  bodySmall: {
    fontFamily: 'Inter-Regular',
    fontSize: 13,
    lineHeight: 18,
    fontWeight: '400' as const,
  },

  // Label (Sans-serif Medium) - Buttons, chips, tabs
  labelLarge: {
    fontFamily: 'Inter-Medium',
    fontSize: 14,
    lineHeight: 20,
    fontWeight: '500' as const,
  },
  labelMedium: {
    fontFamily: 'Inter-Medium',
    fontSize: 12,
    lineHeight: 16,
    fontWeight: '500' as const,
  },

  // Caption - Timestamps, hints
  caption: {
    fontFamily: 'Inter-Regular',
    fontSize: 12,
    lineHeight: 16,
    fontWeight: '400' as const,
  },

  // Caption bold variant
  captionBold: {
    fontFamily: 'Inter-SemiBold',
    fontSize: 12,
    lineHeight: 16,
    fontWeight: '600' as const,
  },

  // Convenience aliases
  body: {
    fontFamily: 'Inter-Regular',
    fontSize: 14,
    lineHeight: 20,
    fontWeight: '400' as const,
  },
  titleSmall: {
    fontFamily: 'Inter-Medium',
    fontSize: 13,
    lineHeight: 18,
    fontWeight: '500' as const,
  },
  labelSmall: {
    fontFamily: 'Inter-Medium',
    fontSize: 11,
    lineHeight: 14,
    fontWeight: '500' as const,
  },

  // Step number (Serif) - Decorative cooking step numbers
  stepNumber: {
    fontFamily: 'Newsreader-Italic',
    fontSize: 56,
    lineHeight: 64,
    fontWeight: '400' as const,
  },
};

// Paprika Spacing
export const PaprikaSpacing = {
  xs: 4,
  sm: 8,
  md: 16,
  lg: 24,
  xl: 32,
  xxl: 48,
};

/**
 * Paprika Layout — how wide content is allowed to get.
 *
 * The phone layout has no upper bound of its own, so on a tablet it stretched to
 * the full viewport while every image inside it kept a hardcoded pixel height:
 * an 820px iPad rendered the planner meal photo at 788 x 150, a 5.25:1 letterbox
 * where the phone shows 2.4:1 (#632). Capping the column fixes both halves of
 * that — text stops running to 788px, and an `aspectRatio` on the photo now has
 * a bounded width to resolve against, so no per-surface height cap is needed.
 *
 * Phones are narrower than `contentMax`, so this is a no-op below 720.
 */
export const PaprikaLayout = {
  /** Max width of a single-column content area (planner, bottom sheets). */
  contentMax: 720,
  /**
   * Above this, treat the device as a tablet and step fixed-size thumbnails up.
   * Sits between the widest phone (~430) and the narrowest tablet (768).
   */
  tabletMinWidth: 600,
} as const;

// Paprika Border Radius
export const PaprikaRadius = {
  none: 0,
  sm: 8,      // Small chips, tags
  md: 12,     // Cards, inputs
  lg: 16,     // Larger cards, modals
  xl: 24,     // Buttons, pills
  full: 999,  // Circular elements, avatars
};

// Paprika Shadows - warm-tinted ambient light
export const PaprikaShadows = {
  card: Platform.select({
    ios: {
      shadowColor: '#52443c',
      shadowOffset: { width: 0, height: 2 },
      shadowOpacity: 0.08,
      shadowRadius: 8,
    },
    android: {
      elevation: 2,
    },
    default: {
      boxShadow: '0 2px 8px rgba(82, 68, 60, 0.08)',
    },
  }) as any,
  editorial: Platform.select({
    ios: {
      shadowColor: '#52443c',
      shadowOffset: { width: 0, height: 24 },
      shadowOpacity: 0.08,
      shadowRadius: 48,
    },
    android: {
      elevation: 6,
    },
    default: {
      boxShadow: '0 24px 48px -12px rgba(82, 68, 60, 0.08)',
    },
  }) as any,
  cardHover: Platform.select({
    ios: {
      shadowColor: '#52443c',
      shadowOffset: { width: 0, height: 4 },
      shadowOpacity: 0.08,
      shadowRadius: 12,
    },
    android: {
      elevation: 4,
    },
    default: {
      boxShadow: '0 4px 12px rgba(82, 68, 60, 0.08)',
    },
  }) as any,
  modal: Platform.select({
    ios: {
      shadowColor: '#52443c',
      shadowOffset: { width: 0, height: -4 },
      shadowOpacity: 0.08,
      shadowRadius: 16,
    },
    android: {
      elevation: 8,
    },
    default: {
      boxShadow: '0 -4px 16px rgba(82,68,60,0.08)',
    },
  }) as any,
};

/** Pantry redesign — freshness state palette (warm, never black). */
export const PaprikaFreshness = {
  expired: { text: '#b23524', bg: '#fbe9e6', soft: '#f4cdc5', dot: '#c0402b' }, // action tones only ("Jeté")
  urgent:  { text: '#b7541f', bg: '#fbe6d5', soft: '#f4cfae', dot: '#e0742f' },
  soon:    { text: '#8a6a1c', bg: '#f7edd4', soft: '#ecd79c', dot: '#cf9e2f' },
  fresh:   { text: '#527038', bg: '#eaf0e0', soft: '#cfe0b4', dot: '#78a55a' },
  stable:  { text: '#8a786d', bg: '#f2ece4', soft: '#e2d5c8', dot: '#bca99b' },
  review:  { text: '#6d44b8', bg: '#efe8fb', soft: '#dccdf3', dot: '#7a4cc0' }, // = AI purple family
} as const;

/** Pantry redesign — the 4 physical spaces ("matière & température"). */
export const PaprikaZones = {
  fridge:  { accent: '#4d7a88', tint: '#f4fafb', cap: '#e4f0f2', gradStart: '#f2fafb', gradEnd: '#dbeef1', ink: '#3c6773' },
  freezer: { accent: '#3f8296', tint: '#eef8fa', cap: '#daecf0', gradStart: '#eaf7fa', gradEnd: '#cfe7ee', ink: '#356f84' },
  counter: { accent: '#a9762f', tint: '#fdf7ec', cap: '#f3e6cc', gradStart: '#fdf6e9', gradEnd: '#f0e0c4', ink: '#8a6224' },
  pantry:  { accent: '#8a5934', tint: '#faf2e7', cap: '#eeddc8', gradStart: '#f8efe2', gradEnd: '#ecd9bf', ink: '#7c4e2b' },
} as const;

/**
 * Recipe placeholder — the tones behind a recipe that has no usable photo.
 *
 * `wash` is the pale gradient the monogram sits on, `ink` the monogram itself,
 * `dot` the small diet marker. Deliberately far lighter than the shelf card's
 * `GRADIENT_TONES`: a browse-grid disc is up to 180pt across, so the same
 * saturation that reads as a cover at 108pt reads as a colour block at 180pt.
 *
 * Keyed by `DietKind` from `src/utils/dietClassifier` — never by a local
 * ingredient-keyword ladder.
 */
export const PaprikaDietWash = {
  veg:  { washStart: '#eef1e4', washEnd: '#dde4cb', ink: '#5d7c43', dot: '#5d7c43' },
  fish: { washStart: '#fbeee7', washEnd: '#f3d9cc', ink: '#b25840', dot: '#cf6f56' },
  meat: { washStart: '#faeede', washEnd: '#f0dcc2', ink: '#9a4a22', dot: '#864e2c' },
} as const;

// Paprika Button Styles
export const PaprikaButtons = {
  primary: {
    backgroundColor: '#864e2c',
    textColor: '#FFFFFF',
    borderRadius: 24,
    paddingVertical: 14,
    paddingHorizontal: 24,
  },
  secondary: {
    backgroundColor: 'transparent',
    textColor: '#1b1c1a',
    borderColor: '#d7c2b8',
    borderWidth: 1,
    borderRadius: 24,
    paddingVertical: 14,
    paddingHorizontal: 24,
  },
  accent: {
    backgroundColor: '#864e2c',
    textColor: '#FFFFFF',
    borderRadius: 24,
    paddingVertical: 14,
    paddingHorizontal: 24,
  },
  destructive: {
    backgroundColor: 'transparent',
    textColor: '#ba1a1a',
    borderRadius: 24,
    paddingVertical: 14,
    paddingHorizontal: 24,
  },
};

// App Typography - Unified typography system
// Uses Newsreader (serif) for editorial headers, Inter (sans-serif) for body
export const AppTypography = {
  // Display styles (Serif - Newsreader) - For main screen headers
  displayLarge: {
    fontFamily: 'Newsreader-Bold',
    fontSize: 32,
    lineHeight: 40,
  },
  displayMedium: {
    fontFamily: 'Newsreader-Bold',
    fontSize: 28,
    lineHeight: 36,
  },

  // Heading styles (Serif - Newsreader) - For section headers
  headingLarge: {
    fontFamily: 'Newsreader-Bold',
    fontSize: 24,
    lineHeight: 32,
  },
  headingMedium: {
    fontFamily: 'Newsreader-Bold',
    fontSize: 20,
    lineHeight: 28,
  },

  // Title styles (Sans-serif - Inter) - For card titles, list items
  titleLarge: {
    fontFamily: 'Inter-SemiBold',
    fontSize: 17,
    lineHeight: 24,
  },
  titleMedium: {
    fontFamily: 'Inter-Medium',
    fontSize: 15,
    lineHeight: 22,
  },

  // Body styles (Sans-serif - Inter) - For regular text content
  bodyLarge: {
    fontFamily: 'Inter-Regular',
    fontSize: 16,
    lineHeight: 24,
  },
  bodyMedium: {
    fontFamily: 'Inter-Regular',
    fontSize: 14,
    lineHeight: 20,
  },
  bodySmall: {
    fontFamily: 'Inter-Regular',
    fontSize: 13,
    lineHeight: 18,
  },

  // Label styles (Sans-serif - Inter Medium) - For buttons, chips, tabs
  labelLarge: {
    fontFamily: 'Inter-Medium',
    fontSize: 14,
    lineHeight: 20,
  },
  labelMedium: {
    fontFamily: 'Inter-Medium',
    fontSize: 12,
    lineHeight: 16,
  },

  // Caption style (Sans-serif - Inter) - For meta info, timestamps
  caption: {
    fontFamily: 'Inter-Regular',
    fontSize: 12,
    lineHeight: 16,
  },
  captionBold: {
    fontFamily: 'Inter-SemiBold',
    fontSize: 12,
    lineHeight: 16,
  },

  // Aliases for convenience
  body: {
    fontFamily: 'Inter-Regular',
    fontSize: 14,
    lineHeight: 20,
  },
  titleSmall: {
    fontFamily: 'Inter-Medium',
    fontSize: 13,
    lineHeight: 18,
  },
  labelSmall: {
    fontFamily: 'Inter-Medium',
    fontSize: 11,
    lineHeight: 14,
  },
};

// Smart Inventory Screen Styles — shared across camera, analyzing, results screens
export const PaprikaSmartInventory = {
  glassmorphicHeader: {
    backgroundColor: 'rgba(251,249,246,0.85)',
    height: 64,
    paddingHorizontal: 24,
    flexDirection: 'row' as const,
    justifyContent: 'space-between' as const,
    alignItems: 'center' as const,
  },
  scannerCorner: {
    width: 40,
    height: 40,
    borderColor: 'rgba(255,255,255,0.6)',
  },
  darkFooter: {
    backgroundColor: '#0a0a0a',
  },
  statusPill: {
    backgroundColor: 'rgba(0,0,0,0.4)',
    borderRadius: 999,
    paddingHorizontal: 16,
    paddingVertical: 8,
  },
  headerTitle: {
    fontFamily: 'Newsreader-Regular',
    fontSize: 18,
    color: '#1b1c1a',
  },
  headerTitleAccent: {
    fontFamily: 'Newsreader-Italic',
    fontSize: 20,
    color: '#864e2c',
  },
  headerButton: {
    padding: 8,
    borderRadius: 999,
  },
};

